// iPad-safe field editor — a bottom sheet with a native <input> that
// stays mounted regardless of PDF re-renders. Opening the sheet blurs
// the PDF layer so the iOS keyboard has no interactive elements above
// it to steal focus. Closing commits the value to the parent via
// onDone; Cancel discards.
import React, { useEffect, useRef, useState } from "react";
import { X, Check } from "@phosphor-icons/react";

export default function FieldEditorSheet({ open, field, initialValue, onDone, onCancel }) {
  const [value, setValue] = useState("");
  const inputRef = useRef(null);
  useEffect(() => {
    if (open) {
      setValue(initialValue ?? "");
      // Small delay lets iOS commit the transform before we focus.
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open, initialValue]);

  if (!open || !field) return null;
  const commit = () => onDone?.(value);
  const cancel = () => onCancel?.();

  return (
    <>
      <div
        onClick={cancel}
        style={{ position: "fixed", inset: 0, background: "rgba(1,35,64,0.4)", zIndex: 999 }}
      />
      <div
        data-testid="field-editor-sheet"
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1000,
          background: "#fff", borderTopLeftRadius: 18, borderTopRightRadius: 18,
          boxShadow: "0 -8px 40px rgba(1,35,64,0.28)",
          padding: "20px 20px 28px",
          maxWidth: 720, margin: "0 auto",
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: "0.28em", fontWeight: 800, color: "#5C738A", textTransform: "uppercase" }}>{field.type} field</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: "#012340", marginTop: 4, marginBottom: 14 }}>{field.name}</div>
        {field.type === "date" ? (
          <input
            ref={inputRef}
            type="date"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid="field-editor-input"
            style={{ width: "100%", fontSize: 20, padding: "14px 14px", border: "1px solid #cfd6dd", borderRadius: 8, fontFamily: "inherit", boxSizing: "border-box" }}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
            placeholder={field.name}
            data-testid="field-editor-input"
            autoCapitalize="words"
            autoComplete="off"
            spellCheck={false}
            style={{ width: "100%", fontSize: 20, padding: "14px 14px", border: "1px solid #cfd6dd", borderRadius: 8, fontFamily: "inherit", boxSizing: "border-box" }}
          />
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button
            onClick={cancel}
            data-testid="field-editor-cancel"
            style={{ flex: 1, padding: "14px", background: "#fff", color: "#5C738A", border: "1px solid #cfd6dd", borderRadius: 8, fontSize: 13, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <X size={14} weight="bold" /> Cancel
          </button>
          <button
            onClick={commit}
            data-testid="field-editor-done"
            style={{ flex: 2, padding: "14px", background: "#012340", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            <Check size={14} weight="bold" /> Done
          </button>
        </div>
      </div>
    </>
  );
}
